/** Customer analytics query hooks (C1–C11 + diagnostics). */
import { useQuery } from "@tanstack/react-query";

import {
  fetchBillPeer,
  fetchBillProjection,
  fetchBudgetGoal,
  fetchConsumptionProfile,
  fetchConsumptionTrend,
  fetchDiagnostics,
  fetchMeteringPriority,
  fetchPeerComparison,
  fetchSavings,
  fetchShadowBill,
  fetchTierAlert,
} from "@/features/customer/api";
import { thresholds } from "@/lib/thresholds";

const MIN = 10 * 60_000;
const q = <T>(
  id: string,
  key: string,
  fn: (id: string, s?: AbortSignal) => Promise<T>,
) =>
  ({
    queryKey: ["customer", id, key],
    queryFn: ({ signal }: { signal: AbortSignal }) => fn(id, signal),
    staleTime: MIN,
    enabled: !!id,
  }) as const;

export const useShadowBill = (id: string) =>
  useQuery(q(id, "shadow-bill", fetchShadowBill));
export const useTierAlert = (id: string) =>
  useQuery(q(id, "tier-alert", fetchTierAlert));
export const usePeerComparison = (id: string) =>
  useQuery(q(id, "peer", fetchPeerComparison));
export const useConsumptionProfile = (id: string) =>
  useQuery(q(id, "profile", fetchConsumptionProfile));
export const useConsumptionTrend = (id: string) =>
  useQuery(q(id, "trend", fetchConsumptionTrend));
export const useBillProjection = (id: string) =>
  useQuery(q(id, "projection", fetchBillProjection));
export const useSavings = (id: string) => useQuery(q(id, "savings", fetchSavings));
export const useBudgetGoal = (id: string) => useQuery(q(id, "goal", fetchBudgetGoal));
export const useBillPeer = (id: string) => useQuery(q(id, "bill-peer", fetchBillPeer));
export const useDiagnostics = (id: string) =>
  useQuery(q(id, "diagnostics", fetchDiagnostics));

// Fleet-wide, not per-sensor — its own query key shape (no `id`).
export const useMeteringPriority = (
  days = thresholds().default_analysis_days,
  locality: string | null = null,
) =>
  useQuery({
    queryKey: ["customer", "metering-priority", days, locality],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      fetchMeteringPriority(days, locality, signal),
    staleTime: MIN,
  });
